import { createMachine, interpret, assign } from "@xstate/fsm";
import { update, hide, show, setStyle } from "@ideal-postcodes/jsutil";
/**
 * @hidden
 */
const INPUT = { INPUT: { actions: "input" } };
/**
 * @hidden
 */
const CLOSE = { CLOSE: "closed" };
/**
 * @hidden
 */
const SUGGEST = {
    SUGGEST: { target: "suggesting", actions: ["updateSuggestions"] },
};
/**
 * @hidden
 */
const NOTIFY = {
    NOTIFY: { target: "notifying", actions: ["updateMessage"] },
};
/**
 * Creates a finite state machine that drives Address Finder UI
 */
export const create = ({ view }) => {
    const machine = createMachine({
        initial: "closed",
        context: {
            suggestions: [],
            message: view.options.msgInitial,
            current: -1,
        },
        states: {
            closed: {
                entry: ["close"],
                exit: ["open"],
                on: {
                    AWAKE: [
                        {
                            target: "suggesting",
                            cond: (c) => c.suggestions.length > 0,
                        },
                        {
                            target: "notifying",
                        },
                    ],
                },
            },
            notifying: {
                entry: ["renderNotice"],
                exit: ["clearAnnouncement"],
                on: {
                    ...CLOSE,
                    ...SUGGEST,
                    ...NOTIFY,
                    ...INPUT,
                },
            },
            suggesting: {
                entry: ["renderSuggestions", "gotoCurrent", "expand"],
                exit: ["resetCurrent", "gotoCurrent", "contract"],
                on: {
                    ...CLOSE,
                    ...SUGGEST,
                    ...NOTIFY,
                    ...INPUT,
                    NEXT: { actions: ["next", "gotoCurrent"] },
                    PREVIOUS: { actions: ["previous", "gotoCurrent"] },
                    RESET: { actions: ["resetCurrent", "gotoCurrent"] },
                    SELECT: { target: "closed", actions: ["select"] },
                },
            },
        },
    }, {
        actions: {
            /**
             * Updates current li in list to active descendant
             */
            gotoCurrent: (c) => {
                const lis = view.list.children;
                view.input.setAttribute("aria-activedescendant", "");
                for (let i = 0; i < lis.length; i += 1) {
                    if (i === c.current) {
                        view.input.setAttribute("aria-activedescendant", lis[i].id);
                        lis[i].setAttribute("aria-selected", "true");
                        view.goto(i);
                    }
                    else {
                        lis[i].setAttribute("aria-selected", "false");
                    }
                }
            },
            /**
             * Unhighlights a suggestion
             */
            resetCurrent: assign({ current: -1 }),
            /**
             * Triggers onInput callback
             */
            input: (_, e) => {
                if (e.type !== "INPUT")
                    return;
                view.options.onInput.call(view, e.event);
            },
            /**
             * Clears ARIA announcement fields
             */
            clearAnnouncement: () => view.announce(""),
            /**
             * Renders suggestion within list
             */
            renderSuggestions: (c, e) => {
                if (e.type !== "SUGGEST")
                    return;
                view.list.innerHTML = "";
                const id = view.list.id;
                const s = c.suggestions;
                s.forEach(({ suggestion }, i) => {
                    const li = view.options.document.createElement("li");
                    li.textContent = suggestion;
                    li.setAttribute("aria-selected", "false");
                    li.setAttribute("tabindex", "-1");
                    li.setAttribute("aria-posinset", `${i + 1}`);
                    li.setAttribute("aria-setsize", s.length.toString());
                    li.setAttribute("role", "option");
                    setStyle(li, view.options.liStyle);
                    li.id = `${id}_${i}`;
                    view.list.appendChild(li);
                });
                view.announce(`${s.length} addresses available`);
            },
            /**
             * Update context.suggestions
             */
            updateSuggestions: assign({
                suggestions: (c, e) => {
                    if (e.type !== "SUGGEST")
                        return c.suggestions;
                    return e.suggestions;
                },
                current: () => -1,
            }),
            /**
             * Hides list and runs callback
             */
            close: (_, e) => {
                let reason = "blur";
                if (e.type === "CLOSE")
                    reason = e.reason;
                hide(view.list);
                if (e.type === "CLOSE" && e.reason === "esc")
                    update(view.input, "");
                view.options.onClose.call(view, reason);
            },
            /**
             * Makes list visible and run callback
             */
            open: () => {
                show(view.list);
                view.options.onOpen.call(view);
            },
            /**
             * Marks aria component as expanded
             */
            expand: () => {
                view.ariaAnchor().setAttribute("aria-expanded", "true");
            },
            /**
             * Marks aria component as closed
             */
            contract: () => {
                view.ariaAnchor().setAttribute("aria-expanded", "false");
            },
            /**
             * Assigns context.message
             */
            updateMessage: assign({
                message: (c, e) => {
                    if (e.type !== "NOTIFY")
                        return c.message;
                    return e.message;
                },
            }),
            /**
             * Renders message container and current message
             */
            renderNotice: (c) => {
                view.list.innerHTML = "";
                view.input.setAttribute("aria-activedescendant", "");
                view.message.textContent = c.message;
                view.announce(c.message);
                view.list.appendChild(view.message);
            },
            /**
             * Selects next element in list. Wraps to top if at bottom
             */
            next: assign({
                current: (c) => c.current + 1 > view.list.children.length - 1
                    ? 0 // Wrap to first elem
                    : c.current + 1,
            }),
            /**
             * Selects previous element in list. Wraps to bottom if at top
             */
            previous: assign({
                current: (c) => c.current - 1 < 0
                    ? view.list.children.length - 1 // Wrap to last elem
                    : c.current - 1,
            }),
            /**
             * Triggers select on current suggestion or clicked element
             */
            select: (_, e) => {
                if (e.type !== "SELECT")
                    return;
                view.options.onSelect.call(view, e.suggestion);
                view.announce(`The address ${e.suggestion.suggestion} has been applied to this form`);
            },
        },
    });
    return interpret(machine);
};
